/**
 * Truncates a string in the middle if it exceeds the specified length.
 * 
 * @param {string} n - The string to truncate.
 * @param {number} len - The maximum allowed length.
 * @returns {string} The truncated string or the original if within limit.
 */
export function truncate(n, len) {
    return n && n.length > len
        ? `${n.substr(0, len / 2 - 1)}...${n.substr(-(len / 2 + 1))}`
        : n || "";
}


/**
 * Truncates a string at the end if it exceeds the specified length.
 * 
 * @param {string} n - The string to truncate.
 * @param {number} len - The maximum allowed length.
 * @returns {string} The truncated string with ".." or the original if within limit.
 */
export function truncateEnd(n, len) {
    return n && n.length > len ? `${n.substr(0, len)}..` : n || "";
}

/**
 * Truncates a string if it exceeds the specified limit, adding ".." at the end.
 * 
 * @param {string} str - The string to truncate.
 * @param {number} limit - The maximum allowed length.
 * @returns {string} The truncated string or the original if within the limit.
 */
export function limitString(str, limit) {
    return str.length > limit ? str.slice(0, limit - 2) + ".." : str;
}


/**
 * Capitalizes the first letter of each word in a string.
 * 
 * @param {string} words - The input string.
 * @returns {string} The formatted string with each word capitalized.
 */
export function capitalizeTheFirstLetterOfEachWord(words) {
    return words
        .toLowerCase()
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

/**
 * Capitalizes the first letter of a string.
 * 
 * @param {string} string - The input string.
 * @returns {string} The string with the first letter capitalized.
 */
export function capitalizeFirstLetter(string) {
    return string ? string.charAt(0).toUpperCase() + string.slice(1) : "";
}

/**
 * Splits a string into two halves.
 * 
 * @param {string} string - The input string.
 * @returns {{ first: string, last: string }} An object containing both halves.
 */
export function splitStringInHalf(string) {
    const middle = Math.floor(string.length / 2);
    return { first: string.slice(0, middle), last: string.slice(middle) };
}

/**
 * Converts a string to a regular expression, removing leading/trailing slashes if present.
 * 
 * @param {string} regexString - The string to be converted into a regex.
 * @returns {RegExp} - The resulting regular expression.
 */
export function stringToRegex(regexString) {
    return new RegExp(regexString.replace(/^\/|\/$/g, ''));
}

/**
* Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
*
* @param {String} text The text to be rendered.
* @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
*
* @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
*/
export function getTextWidth(text, font) {
    // re-use canvas object for better performance
    const canvas =
        getTextWidth.canvas ||
        (getTextWidth.canvas = document.createElement("canvas"));
    const context = canvas.getContext("2d");
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
}

/**
 * Capitalizes the first character of a string.
 * 
 * @param {string} string - The string to capitalize.
 * @returns {string} - The capitalized string.
 */
export function ucfirst(string) {
    return string[0].toUpperCase() + string.slice(1);
}


/**
 * Generates a random string of a given length.
 * 
 * @param {number} length - The length of the string to generate.
 * @returns {string} - The generated random string.
 */
export function generateRandomString(length) {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    while (length--) result += characters[Math.floor(Math.random() * characters.length)];
    return result;
}

/**
 * Validates if a URL starts with "http://" or "https://".
 * 
 * @param {string} value - The URL to validate.
 * @returns {boolean} - Returns `true` if the URL starts with "http://" or "https://", otherwise `false`.
 */
export function validateUrlProtocol(value) {
    return /^(https?:\/\/)/i.test(value);
}

export const parseRangeString = (rangeStr) => {
    const pages = new Set();
    const parts = rangeStr.split(',');

    parts.forEach(part => {
        if (part.includes('-')) {
            const [start, end] = part.split('-').map(Number);
            for (let i = start; i <= end; i++) {
                pages.add(i);
            }
        } else {
            pages.add(Number(part));
        }
    });

    return Array.from(pages);
}
